import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { ToastContainer } from "react-toastify";
import NotificationAlertOptions from "@/layouts/Alerts";
import "@/assets/css/FormValidation.css";
import { addPage, getPagesByCurrentSite } from "@/services/pageServices/pageAction";
import { refusePageTypePayload } from "@/services/pageServices/pagesSlice";
import {
  getProductProperties,
  getProductPropertiesById,
} from "@/services/productPropertiesServices/ProductPropertiesActions";
import { getAllProperties } from "@/services/productServices/productActions";
import { getTemplatesByDesign } from "@/services/templateServices/templateAction";
import { getBlog, getPosts } from "@/services/blogServices/blogActions";
import { getCategories } from "@/services/categoryServices/categoryAction";
import { getAds } from "@/services/adServices/adActions";
import { getUsers } from "@/services/userServices/userActions";
import {
  findOverlayRow,
  getTaxonomyOverlays,
  overlayPathOf,
  type TaxonomyOverlayRow,
} from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";
import PageFormView from "./PageFormView";
import { pageFormValidation } from "./pageFormValidation";
import { pageFormWizard, type PageFormState, type PageTypeSelection } from "./pageFormWizard";
import type { PageTypeConfiguration } from "./PageTypesCreateContainer";
import { asCaught } from "@/services/caughtError";

const EMPTY: TaxonomyOverlayRow[] = [];
const validationActions = pageFormValidation.actions;
const wizardActions = pageFormWizard.actions;

const PageFormContainer = () => {
  const dispatch = useAppDispatch();

  const Currentsite = useAppSelector((s) => s.sites.site);
  // SCRUM-1809: the taken-path set reads every page of the site from
  // `GET /api/pages/pagesBySite`, not the site document's 50-at-a-time `pages`,
  // so a path already used by a page in a later batch is refused rather than
  // accepted and then rejected by the server.
  const sitePages = useAppSelector((s) => s.pages.pages) || [];
  const subcategoriesById = useAppSelector((s) => s.categories.pageTypeSubcategories);
  const properties = useAppSelector((s) => s.products.properties ?? EMPTY);

  // SCRUM-2267: the step, the page draft, the page-type pick, the two step keys
  // and the typed entity path are one createSlice, mounted locally. The pick
  // arrives through onConfigurationChange only (SCRUM-2206); page_configuration
  // is derived from it below.
  const [wizard, dispatchWizard] = useReducer(
    pageFormWizard.reducer,
    pageFormWizard.getInitialState()
  );
  const { currentStep, page, pageTypeSelection, templateStepKey, pageTypeStepKey, typedEntityPath } =
    wizard;
  // SCRUM-2266: the five validation flags are one createSlice, mounted locally.
  const [validation, dispatchValidation] = useReducer(
    pageFormValidation.reducer,
    pageFormValidation.getInitialState()
  );
  const { formchecker, showCheckerResult, isFormValid, isValidationAttempted, formHasError } =
    validation;
  // SCRUM-1575: `typedEntityPath` is kept apart from page_configuration because
  // that is derived anew from the pick on every render, and once the derived
  // value sits in there it is indistinguishable from a typed one.
  // SCRUM-1704: the site's TaxonomyOverlay rows (SCRUM-1703) — the per-site
  // label and full-path slug the migration engine resolves on. A category /
  // sub_category page takes its path from the node's row, whole; the `label[]`
  // composition below stays only as the fallback for a node without one.
  // Fetched once per site, not per pick: the rows are the site's, and a fetch
  // per pick would race the editor to the Next button.
  const [siteOverlays, setSiteOverlays] = useState(EMPTY);

  useEffect(() => {
    const siteId = Currentsite?._id;
    if (!siteId) return undefined;
    let cancelled = false;
    getTaxonomyOverlays(siteId)
      .then((rows) => {
        if (!cancelled) setSiteOverlays(rows);
      })
      .catch(() => {
        // No rows is the fallback branch, not an error the wizard can act on.
        if (!cancelled) setSiteOverlays(EMPTY);
      });
    return () => {
      cancelled = true;
    };
  }, [Currentsite?._id]);

  const existingPaths = new Set(
    sitePages.map((p) => p?.page_configuration?.path).filter(Boolean)
  );

  // SCRUM-1809: load the full list when the store is empty, the shape
  // `useDropdownOptions.js` uses. Empty means "not loaded yet", so until the
  // answer lands no path is treated as taken — which is what the wizard did
  // before while the site batches were still arriving.
  useEffect(() => {
    if (!sitePages.length) dispatch(getPagesByCurrentSite());
  }, [dispatch, sitePages.length]);

  // The template step's data: once per wizard, not per template-step reset.
  useEffect(() => {
    dispatch(getProductProperties());
    dispatch(getBlog());
    dispatch(getPosts());
    dispatch(getCategories());
    dispatch(getAds());
    dispatch(getUsers());
    dispatch(getPagesByCurrentSite());
  }, [dispatch]);

  // The templates follow the site's design, which can arrive after mount
  // (SCRUM-571). Guarded so no /api/templates/bydesign/undefined (SCRUM-1438).
  const siteDesignId = Currentsite?.design?._id;
  useEffect(() => {
    if (siteDesignId) dispatch(getTemplatesByDesign(siteDesignId));
  }, [dispatch, siteDesignId]);

  const clampTrailingSlash = (p) => (p.endsWith("/") ? p : `${p}/`);
  // SCRUM-692 (reopened 2026-08-26): trim BEFORE slugging. Without it every
  // space becomes a dash, so a routing path of only spaces left here as "-/" or
  // "---/" — not "" and not "/", so it slipped past the step-0 gate below AND
  // past the server guard, and the page landed on a junk path. Trimming maps
  // whitespace-only back to "", which clamps to "/" — the one shape both
  // existing guards already refuse, so this closes the hole without inventing a
  // second rule about what counts as a meaningless path.
  //
  // Interior spaces are untouched on purpose: "a b" -> "a-b/" is what
  // slugification is for. What also changes, deliberately: leading/trailing
  // spaces no longer survive as edge dashes, so "  a  " now yields "a/" instead
  // of "--a--/". That applies to EVERY caller below, not just the typed
  // simple_page field — a category/sub_category/post path built from a per-site
  // label with stray edge whitespace now computes without the edge dashes too.
  // Create-only (this component only ever calls addPage), so no stored path is
  // recomputed; only newly created pages take the tidier form.
  //
  // Known limit, unchanged by this fix: the split is on " " alone, so an
  // interior TAB survives verbatim ("a\tb" -> "a\tb/").
  const toSlug = (s) => s?.trim().split(" ").join("-").toLowerCase();

  const computeConfiguration =
    (cfg, typedPath = "") => {
      if (!cfg || !Currentsite?._id) return null;

      const configuration = {
        type: cfg.type,
        object_target: cfg.object,
        path: "",
      };

      // SCRUM-1575: a path the editor typed on the create wizard wins over the
      // derived one for the two entity types. Same toSlug / clampTrailingSlash
      // as the simple_page field, so a whitespace-only value slugs to "" and
      // falls through to the derived default instead of landing on "/".
      const typedSlug = toSlug(typedPath);
      if (typedSlug && ["category", "sub_category"].includes(cfg.type)) {
        return { ...configuration, path: clampTrailingSlash(typedSlug) };
      }

      if (cfg.type === "simple_page") {
        const p = clampTrailingSlash(toSlug(cfg.object || ""));
        return { ...configuration, object_target: {}, path: p };
      }

      if (["error", "maintenance", "redirect"].includes(cfg.type)) {
        return { ...configuration, object_target: {}, path: clampTrailingSlash(toSlug(cfg.object || "")) };
      }

      if (cfg.type === "category") {
        // SCRUM-1704: the overlay slug is the whole site-relative path.
        const overlayPath = overlayPathOf(
          findOverlayRow(siteOverlays, "category", cfg.object?._id)
        );
        if (overlayPath) {
          return { ...configuration, path: clampTrailingSlash(overlayPath) };
        }
        // SCRUM-1851: no row, no second source — the node's own slug. The
        // stored `label[]` it used to fall back to leaves the schema with
        // SCRUM-1716 and was already stale beside the row.
        return { ...configuration, path: clampTrailingSlash(toSlug(cfg.object?.slug || "")) };
      }

      if (cfg.type === "sub_category") {
        const sc = cfg.object;
        // SCRUM-1704: read the overlay path once, never prepend the category
        // to it — a subcategory overlay slug already carries its category
        // segment (`bankrekening/student`), so the composition below would
        // double the prefix the moment a row exists. It stays for nodes
        // without a row.
        const overlayPath = overlayPathOf(
          findOverlayRow(siteOverlays, "subcategory", sc?._id)
        );
        if (overlayPath) {
          return { ...configuration, path: clampTrailingSlash(overlayPath) };
        }
        // SCRUM-1851: no row — the category's and the node's own slugs, never
        // the stored `label[]` entries (gone with SCRUM-1716).
        const finalPath = clampTrailingSlash(
          `${toSlug(sc?.category?.slug || "")}/${toSlug(sc?.slug || "")}`
        );
        return { ...configuration, path: finalPath };
      }

      if (cfg.type === "product") {
        const prod = cfg.object;
        let finalPath = "";

        const subcat = (subcategoriesById || []).find((x) => x._id === prod?.subcategory);
        const productPath = String(prod?.path || "");
        const lastElement = toSlug(productPath.split("/").slice(-1)[0] || "");
        const restElements = (productPath.split("/")[0] || "").toLowerCase();

        if (subcat?.category) {
          // SCRUM-1851: the category's site path is its overlay row's slug
          // (the whole site-relative path, SCRUM-1673) — until now the
          // lower-cased `label` of its stored `label[]` entry.
          const catLabelPath = overlayPathOf(
            findOverlayRow(siteOverlays, "category", subcat.category?._id)
          );
          if (catLabelPath) {
            finalPath = clampTrailingSlash(`${catLabelPath}/${lastElement}`);
          } else {
            finalPath = clampTrailingSlash(`${restElements}/${lastElement}`);
          }
        } else {
          finalPath = clampTrailingSlash(`${restElements}/${lastElement}`);
        }
        return { ...configuration, path: finalPath };
      }

      if (cfg.type === "post") {
        return { ...configuration, path: clampTrailingSlash(toSlug(cfg.object?.pathpost || "")) };
      }

      if (cfg.type === "author") {
        const raw = (cfg.object?.pathauthor || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        return { ...configuration, path: clampTrailingSlash(toSlug(raw)) };
      }
     

      if (cfg.type === "default") {
        return { type: "", path: "", object_target: {} };
      }

      return configuration;
    };

  // SCRUM-1704: the notice under the routing-path field when the derived path
  // came from the node's own slug rather than from an overlay row (since
  // SCRUM-1851 there is no `label[]` step in between) — "" when the node has
  // a row, or the type derives no entity path at all. Kept out of
  // `page_configuration` on purpose: it is a remark about the derivation, not
  // a field of the page, and everything in that object is posted.
  const derivedPathNotice = (() => {
    const cfg = pageTypeSelection;
    if (!cfg || (cfg.type !== "category" && cfg.type !== "sub_category")) return "";
    const nodeKind = cfg.type === "category" ? "category" : "subcategory";
    const row = findOverlayRow(siteOverlays, nodeKind, cfg.object?._id);
    if (overlayPathOf(row)) return "";
    return `No site label yet for this ${nodeKind} — path derived from the global slug`;
  })();

  // SCRUM-2206: page_configuration is derived here, during render, and nowhere
  // else. Its inputs can change after the pick — the overlay rows and the
  // site arrive asynchronously, the editor types a path — so it is computed
  // from them rather than stored at the pick. Until this ticket an effect wrote
  // it from a store copy of the pick, on top of the handler's own write.
  // (No useMemo. Note: the React Compiler skips this component, SCRUM-2267 —
  // `CompileError: Support value blocks ... within a try/catch statement`, the
  // try in savePage — so nothing here is memoised; a plain call is cheap.)
  const deriveConfiguration = (): PageFormState["page_configuration"] => {
    if (!pageTypeSelection) return {};
    return computeConfiguration(pageTypeSelection, typedEntityPath) ?? {
      // No current site yet: the pick as reported, which is what the handler
      // stored before the effect could compute.
      type: pageTypeSelection.type,
      object_target: pageTypeSelection.object,
      path: pageTypeSelection.path,
    };
  };
  const pageDraft: PageFormState = { ...page, page_configuration: deriveConfiguration() };

  const validateForm = () => {
    const title = (page.global_title ?? "").trim();
    const desc = (page.meta_description ?? "").trim();
    const checker = {
      globalTxt: title === "",
      metaDescription: desc === "",
    };
    dispatchValidation(validationActions.checkerSet(checker));
    return !checker.globalTxt && !checker.metaDescription;
  };

  // These two keep a stable identity on purpose: they are passed down as
  // onErrorChange / onFormValidation, and the children list them in effect
  // dependency arrays (PageTypesCreateContainer, IndexTemplateSelector). A new
  // function per render would re-run those effects on every render, and the
  // React Compiler skips this component, so nothing else keeps them stable.
  const handleErrorChange = useCallback((hasError) => {
    dispatchValidation(validationActions.errorChanged(hasError));
  }, [dispatchValidation]);

  const handleFormValidation = useCallback((isValid) => {
    dispatchValidation(validationActions.formValidityChanged(isValid));
  }, [dispatchValidation]);

  const handleNextStep = () => {
    dispatchValidation(validationActions.validationAttempted());

    // Check for duplicate path on step 0 for all page types
    if (currentStep === 0) {
      const path = pageDraft.page_configuration?.path || "";
      // SCRUM-692: a simple_page takes its path from a field the editor types,
      // and an empty one is not empty by the time it leaves here —
      // computeConfiguration runs it through clampTrailingSlash(toSlug("")),
      // which yields "/". That is the site root, and the highest-priority
      // homepage match server-side, so leaving the field blank silently
      // publishes over the homepage. Blocked here rather than on the Save
      // buttons because both of them (Save Page on Step2, Save as draft on
      // Step1) sit behind this gate, so one check covers both.
      //
      // Only simple_page: every other type derives its path from a slug or a
      // per-site label, so a blank there is not something the editor can fix
      // in this form. The server guard is the general one.
      if (pageDraft.page_configuration?.type === "simple_page" && path.trim() === "/") {
        NotificationAlertOptions(
          "error",
          "A routing path is required. Fill in the path for this page."
        );
        return;
      }
      if (path && existingPaths.has(path)) {
        NotificationAlertOptions(
          "error",
          `This path "${path}" already exists. Please delete the existing page or change the path.`
        );
        return;
      }
    }

    const okFirstGate =
      (isFormValid && currentStep === 1) ||
      (currentStep === 0 && !formHasError) ||
      currentStep > 1;

    if (okFirstGate) {
      dispatchWizard(wizardActions.stepAdvanced());
      dispatchValidation(validationActions.stepAdvanced());
    }

    const stillValid = validateForm();
    if (!stillValid && currentStep === 1) {
      dispatchValidation(validationActions.checkerResultShown());
    }
  };

  const handlePreviousStep = () => dispatchWizard(wizardActions.stepBack());

  // TEXT INPUTS → always strings
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatchWizard(wizardActions.pageFieldChanged({ field: name, value: value ?? "" }));
  };

  const handleProductSortChange = (product_sort) =>
    dispatchWizard(wizardActions.pageFieldChanged({ field: "product_sort", value: product_sort }));

  useEffect(() => {
    if (pageTypeSelection?.type === "sub_category") {
      dispatch(getAllProperties());
    }
  }, [dispatch, pageTypeSelection?.type]);

  // CHECKBOX / SWITCH
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    dispatchWizard(wizardActions.pageFieldChanged({ field: name, value: !!checked }));
  };

  const handlePageConfigurationChange =
    (config: PageTypeConfiguration) => {
      const selection: PageTypeSelection = {
        type: config.type,
        object: config.object_target,
        path: config.path || "",
      };
      // SCRUM-855 / SCRUM-1650: a category / sub_category pick without its
      // array is refused with a code, and the previous pick stays — the guard
      // that sat on the store write this pick no longer makes (SCRUM-2206).
      if (refusePageTypePayload(selection)) return;
      // Only the entity types carry a typed path here; every other type reports
      // "" or mirrors its object (simple_page), which computeConfiguration
      // derives anyway. The reducer keeps it for those two only.
      dispatchWizard(wizardActions.pageTypePicked(selection));
    };

  const handlePageTemplateChange = (template) =>
    dispatchWizard(wizardActions.pageFieldChanged({ field: "page_template", value: template }));

  // Kept stable on purpose: passed down as onContentChange, which
  // IndexTemplateSelector lists in an effect dependency array (it calls it with
  // its content on every change). The compiler skips this component, so a new
  // function per render would re-run that effect, and its dispatch, on every render.
  const handlePageContentChange = useCallback((content) => {
    dispatchWizard(wizardActions.pageFieldChanged({ field: "page_content", value: content }));
  }, [dispatchWizard]);

  // SCRUM-2215: the properties come from the answer of this fetch, not from the
  // store. `GET /api/productproperties/:id` answers one document, and the store
  // field it lands in is shared with `getProductPropertiesByProductId`, which
  // leaves an array of another product's rows there. An effect used to copy
  // `store[0].properties` after the post had already been built, so a new
  // product page got no properties, or another product's.
  const fetchProductProperties = async () => {
    const id = pageTypeSelection?.object?._id;
    if (!id) return undefined;
    const action = await dispatch(getProductPropertiesById(id));
    return action?.payload?.properties;
  };

  // SCRUM-1240: one save path, two entry points. `draft` forces
  // publish_statement:false. The draft entry point sits on Step1, before the
  // isFormValid gate in handleNextStep — a draft control on Step2 would be dead
  // code, because Step2 is unreachable while ContentRequired variables are
  // empty, which is exactly the state a draft exists for.
  const savePage =
    async (draft) => {
      let toPost = pageDraft;
      if (pageTypeSelection?.type === "product") {
        const properties = await fetchProductProperties();
        if (properties !== undefined) {
          const cfg = pageDraft.page_configuration;
          toPost = {
            ...pageDraft,
            page_configuration: {
              ...cfg,
              object_target: { ...((cfg.object_target as object) || {}), properties },
            },
          };
        }
      }

      // Title and META stay required on both paths: a draft relaxes
      // page_content only.
      const ok = validateForm();
      if (!ok) {
        dispatchValidation(validationActions.checkerResultShown());
        return;
      }

      // SCRUM-2275: no conditional or `?.` inside the try/catch, so the React
      // Compiler can compile this component; the error toasts live in
      // notifySaveError below the component. The server no longer answers with
      // tagPagesInfo, so the success toast is the same for page and draft.
      const body = draft ? { ...toPost, publish_statement: false } : toPost;
      try {
        await dispatch(addPage(body));
        NotificationAlertOptions("success", "Page saved successfully!");
        dispatchWizard(wizardActions.wizardSaved());
        dispatchValidation(validationActions.wizardReset());
      } catch (err) {
        notifySaveError(err);
      }
    };

  // `e?` only because PageFormView types the handler as `() => void`; the
  // button always passes its click event, so the read below is unchanged.
  const handleSubmit = async (e?: React.SyntheticEvent) => {
    e!.preventDefault();
    await savePage(false);
  };

  const handleSaveDraft = async (e?: React.SyntheticEvent) => {
    e!.preventDefault();
    await savePage(true);
  };

  return (
    <>
      <ToastContainer />
      <PageFormView
        currentStep={currentStep}
        page={pageDraft}
        pageTypeSelection={pageTypeSelection}
        templateStepKey={templateStepKey}
        pageTypeStepKey={pageTypeStepKey}
        formchecker={formchecker}
        showCheckerResult={showCheckerResult}
        isFormValid={isFormValid}
        isValidationAttempted={isValidationAttempted}
        onErrorChange={handleErrorChange}
        onFormValidation={handleFormValidation}
        onNextStep={handleNextStep}
        onPreviousStep={handlePreviousStep}
        onInputChange={handleInputChange}
        onProductSortChange={handleProductSortChange}
        properties={properties}
        onCheckboxChange={handleCheckboxChange}
        onConfigurationChange={handlePageConfigurationChange}
        onTemplateChange={handlePageTemplateChange}
        onContentChange={handlePageContentChange}
        Currentsite={Currentsite}
        derivedPathNotice={derivedPathNotice}
        onSubmit={handleSubmit}
        onSaveDraft={handleSaveDraft}
      />
    </>
  );
};

// SCRUM-2275: moved verbatim out of savePage's catch block.
function notifySaveError(err: unknown) {
  // Handle duplicate path error from API
  const errorData = asCaught(err)?.response?.data;
  if (errorData?.error === "DUPLICATE_PATH") {
    NotificationAlertOptions(
      "error",
      `This path "${errorData.path}" already exists. Please delete the existing page or change the path.`
    );
  } else if (errorData?.error === "DUPLICATE_LISTING") {
    // SCRUM-1670: one published listing per subcategory per site; the
    // server names the page that already holds it (null from the index
    // backstop, when the guard was raced).
    NotificationAlertOptions(
      "error",
      errorData.existingPath
        ? `This subcategory already has a published listing on this site: ${errorData.existingPath}`
        : "This subcategory already has a published listing on this site."
    );
  } else {
    NotificationAlertOptions("error", "Error saving the page.");
  }
}

export default PageFormContainer;
